import { parseNdjsonStream } from './ndjson'
import { getAccessToken } from './auth'
import type { AttachedDoc, ChatRequest, StreamEvent, User } from './types'

const API_BASE = '' // same-origin (vite proxy in dev; FastAPI staticfiles in prod)

/** Build headers with Authorization token if available. */
export function authHeaders(extra?: Record<string, string>): Record<string, string> {
  const headers: Record<string, string> = { ...extra }
  const token = getAccessToken()
  if (token) headers['Authorization'] = `Bearer ${token}`
  return headers
}

/**
 * POST /chat with `stream: true` and yield NDJSON events as they arrive.
 *
 * Caller is expected to handle the event sequence:
 *   sources → token* → usage → done
 *   (or `error` at any point on the server side)
 *
 * Transport-level errors (network, non-2xx) are thrown as `Error`.
 * AbortController.abort() will surface as a DOMException with name
 * 'AbortError'; the caller usually swallows that.
 */
export async function* streamChat(
  req: Omit<ChatRequest, 'stream'>,
  signal?: AbortSignal,
): AsyncGenerator<StreamEvent, void, void> {
  const res = await fetch(`${API_BASE}/chat`, {
    method: 'POST',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ ...req, stream: true }),
    signal,
  })
  if (!res.ok) {
    const text = await res.text().catch(() => '')
    throw new Error(`HTTP ${res.status} ${res.statusText}: ${text || '(no body)'}`)
  }
  if (!res.body) {
    throw new Error('Response has no body (browser does not support streaming?)')
  }
  yield* parseNdjsonStream<StreamEvent>(res.body)
}

/** Quick GET /healthz — used as a connectivity probe on app boot. */
export async function healthz(): Promise<{ status: string; [k: string]: unknown }> {
  const res = await fetch(`${API_BASE}/healthz`)
  if (!res.ok) throw new Error(`/healthz returned HTTP ${res.status}`)
  return res.json()
}

/**
 * GET /me — resolve the current user from the backend.
 *
 * The server reads ``X-User-ID`` (which we don't send yet — that's
 * tomorrow's login flow) and falls back to the seeded ``u_default``,
 * so for the single-tenant demo this always succeeds.  An HTTP 401 is
 * surfaced as a thrown Error so the UI can show a "not logged in"
 * state instead of silently displaying the wrong identity.
 */
export async function fetchMe(): Promise<User> {
  const res = await fetch(`${API_BASE}/me`, { headers: authHeaders() })
  if (!res.ok) {
    const body = await res.text().catch(() => '')
    throw new Error(`HTTP ${res.status} ${res.statusText}: ${body || '(no body)'}`)
  }
  return res.json() as Promise<User>
}

/**
 * POST /uploads — send a single file (PDF / DOCX / TXT / MD) and
 * receive back the persisted `AttachedDoc` metadata.
 *
 * The server caps the request at 20 MB and rejects unsupported
 * extensions with HTTP 415.  For UX, we surface the body of any non-2xx
 * response in the thrown error so the chip can display a useful message
 * ("File too large", "Scanned PDF — run OCR first", etc.).
 *
 * `signal` lets the composer abort an in-flight upload if the user
 * removes the chip while the network round-trip is still pending.
 */
export async function uploadDocument(
  file: File,
  signal?: AbortSignal,
): Promise<AttachedDoc> {
  const form = new FormData()
  form.append('file', file, file.name)

  const res = await fetch(`${API_BASE}/uploads`, {
    method: 'POST',
    headers: authHeaders(),
    body: form,
    signal,
  })

  if (!res.ok) {
    // Backend sends FastAPI's standard `{detail: "..."}` for HTTPException.
    const body = await res.text().catch(() => '')
    let detail = body
    try {
      const parsed = JSON.parse(body) as { detail?: string }
      if (parsed.detail) detail = parsed.detail
    } catch {
      /* not JSON — leave the raw body */
    }
    throw new Error(detail || `HTTP ${res.status} ${res.statusText}`)
  }

  return res.json() as Promise<AttachedDoc>
}

/**
 * DELETE /uploads/{doc_id} — best-effort server-side cleanup.
 *
 * The server is idempotent (returns 204 even if the doc was already
 * gone), so the only failure mode worth surfacing is a network error.
 * We deliberately swallow those: a missed delete just means the
 * background GC will pick it up later, and we'd rather not pop a red
 * chip on the user when their UI state is already correct.
 */
export async function deleteUpload(doc_id: string): Promise<void> {
  try {
    await fetch(`${API_BASE}/uploads/${encodeURIComponent(doc_id)}`, {
      method: 'DELETE',
    })
  } catch {
    /* network blip — server-side GC will mop up eventually */
  }
}

export async function logAIContentConfirmation(params: {
  action_type: 'save' | 'export_word' | 'export_pdf'
  matter_id?: string | null
  conv_id?: string | null
}): Promise<void> {
  try {
    await fetch(`${API_BASE}/audit/ai-content-confirm`, {
      method: 'POST',
      headers: authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(params),
    })
  } catch {
    /* best-effort audit log — don't block user action on failure */
  }
}

// ── Document generation API ──────────────────────────────────────────────────

export interface DocTypeItem {
  key: string
  name_zh: string
  name_en: string
  desc_zh: string
  desc_en: string
}

export interface MatterDocument {
  doc_id: string
  matter_id: string
  owner_user_id: string
  doc_type: string
  title: string
  content: string | null
  status: 'generating' | 'done' | 'failed'
  error_msg: string | null
  sources: Record<string, any>[]
  usage: Record<string, any>
  instructions: string | null
  lang: string
  created_at: string
  updated_at: string
  matter_title?: string | null
}

export async function fetchDocumentTypes(): Promise<DocTypeItem[]> {
  const res = await fetch(`${API_BASE}/matters/documents/document-types`, {
    headers: authHeaders(),
  })
  if (!res.ok) throw new Error(`HTTP ${res.status}`)
  const data = await res.json() as { types: DocTypeItem[] }
  return data.types
}

export async function fetchAllDocuments(
  category?: string,
  limit: number = 200,
): Promise<MatterDocument[]> {
  const params = new URLSearchParams()
  if (category) params.set('category', category)
  params.set('limit', String(limit))
  const res = await fetch(`${API_BASE}/matters/documents/all?${params}`, {
    headers: authHeaders(),
  })
  if (!res.ok) throw new Error(`HTTP ${res.status}`)
  const data = await res.json() as { items: MatterDocument[]; total: number }
  return data.items
}

export async function fetchMatterDocuments(matterId: string): Promise<MatterDocument[]> {
  const res = await fetch(`${API_BASE}/matters/${matterId}/documents`, {
    headers: authHeaders(),
  })
  if (!res.ok) throw new Error(`HTTP ${res.status}`)
  const data = await res.json() as { items: MatterDocument[]; total: number }
  return data.items
}

export async function generateMatterDocument(
  matterId: string,
  docType: string,
  instructions?: string,
  lang: string = 'zh',
  fileIds?: string[],
): Promise<MatterDocument> {
  const res = await fetch(`${API_BASE}/matters/${matterId}/documents/generate`, {
    method: 'POST',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ doc_type: docType, instructions, lang, file_ids: fileIds }),
  })
  if (!res.ok) {
    const body = await res.text().catch(() => '')
    let detail = body
    try {
      const parsed = JSON.parse(body) as { detail?: string }
      if (parsed.detail) detail = parsed.detail
    } catch { /* not JSON */ }
    throw new Error(detail || `HTTP ${res.status}`)
  }
  return res.json() as Promise<MatterDocument>
}

export async function deleteMatterDocument(matterId: string, docId: string): Promise<void> {
  const res = await fetch(`${API_BASE}/matters/${matterId}/documents/${docId}`, {
    method: 'DELETE',
    headers: authHeaders(),
  })
  if (!res.ok) throw new Error(`HTTP ${res.status}`)
}

export async function updateMatterDocumentTitle(
  matterId: string,
  docId: string,
  title: string,
): Promise<MatterDocument> {
  const res = await fetch(`${API_BASE}/matters/${matterId}/documents/${docId}`, {
    method: 'PATCH',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ title }),
  })
  if (!res.ok) throw new Error(`HTTP ${res.status}`)
  return res.json() as Promise<MatterDocument>
}
